import {useApiQuery} from "./useApi";
import {useMutation, useQueryClient} from "react-query";
import axiosClient from "./axios-client";

export type AiUsageData = {
    credits_used: number;
    credits_limit: number;
    credits_remaining: number;
    percentage_used: number;
    is_unlimited: boolean;
    has_ai_subscription: boolean;
    ai_plan_name: string | null;
};

export type AiUsageStatsFeature = {
    name: string;
    description: string | null;
    category: string | null;
    cost_per_call: number;
    calls_count: number;
    total_credits: number;
};

/**
 * Forme réelle de `GET /ai/usage/stats` (AiQuotaService::getUsageStats).
 *
 * Attention : ce payload n'a PAS les mêmes clés que `GET /ai/usage`
 * (`remaining`/`percentage`/`by_feature` ici, `credits_remaining`/
 * `percentage_used` là-bas). Le type déclarait auparavant les clés de l'autre
 * endpoint, si bien que `usage_by_feature` était toujours `undefined` et que le
 * bloc « usage par feature » ne s'affichait jamais.
 *
 * `by_feature` est un dictionnaire indexé par feature_key, pas un tableau, et
 * il contient TOUTES les features actives — y compris celles à zéro appel.
 */
export type AiUsageStats = {
    year_month: string;
    credits_used: number;
    credits_limit: number | null;
    remaining: number | null;
    percentage: number;
    is_unlimited: boolean;
    has_ai_subscription: boolean;
    by_feature: Record<string, AiUsageStatsFeature>;
};

export type AiUsageHistory = {
    history: {
        year_month: string;
        credits_used: number;
        credits_limit: number;
    }[];
};

export type AiFeature = {
    key: string;
    name: string;
    description: string | null;
    cost: number;
    category: string | null;
};

export type AiFeaturesResponse = {
    features: AiFeature[];
};

export type AiPlan = {
    id: number;
    name: string;
    slug: string;
    description: string | null;
    monthly_credits: number | null;
    price_monthly: number;
    price_annual: number;
    is_unlimited: boolean;
    features: string[] | null;
};

export type AiPlansResponse = {
    plans: AiPlan[];
};

export type AiSubscription = {
    id: number;
    plan_id: number;
    plan_name: string;
    monthly_credits: number | null;
    is_unlimited: boolean;
    price_monthly: number;
    price_annual: number;
    billing_period: 'monthly' | 'annual';
    payment_method: 'stripe' | 'invoice';
    status: 'active' | 'cancelled' | 'expired';
    starts_at: string | null;
    ends_at: string | null;
    cancelled_at: string | null;
};

export type AiSubscriptionResponse = {
    subscription: AiSubscription | null;
};

export type SubscribePayload = {
    ai_plan_id: number;
    billing_period: 'monthly';
    payment_method: 'stripe' | 'invoice';
};

export type SubscribeResponse = {
    success?: boolean;
    message?: string;
    url?: string; // URL Stripe Checkout (si payment_method = 'stripe')
    subscription?: {
        id: number;
        plan_name: string;
        monthly_credits: number | null;
        is_unlimited: boolean;
        price: number;
        billing_period: string;
        starts_at: string;
    };
};

// Hooks pour l'usage IA côté utilisateur

export function useAiUsage(facilityId?: number) {
    const url = facilityId
        ? `/ai/usage?facility_id=${facilityId}`
        : `/ai/usage`;
    return useApiQuery<AiUsageData>(url, {}, {
        staleTime: 30000, // 30 secondes
        refetchOnWindowFocus: true,
    });
}

export function useAiUsageStats(facilityId?: number, yearMonth?: string) {
    let url = `/ai/usage/stats`;
    const params: string[] = [];
    if (facilityId) params.push(`facility_id=${facilityId}`);
    if (yearMonth) params.push(`year_month=${yearMonth}`);
    if (params.length > 0) url += `?${params.join('&')}`;

    return useApiQuery<AiUsageStats>(url);
}

export function useAiUsageHistory(facilityId?: number, months: number = 6) {
    let url = `/ai/usage/history?months=${months}`;
    if (facilityId) url += `&facility_id=${facilityId}`;

    return useApiQuery<AiUsageHistory>(url);
}

export function useAiFeatures() {
    return useApiQuery<AiFeaturesResponse>('/ai/features');
}

export function useAiPlans() {
    return useApiQuery<AiPlansResponse>('/ai/plans');
}

export function useAiSubscription() {
    return useApiQuery<AiSubscriptionResponse>('/ai/subscription');
}

export function useSubscribeToAiPlan() {
    const queryClient = useQueryClient();

    return useMutation<SubscribeResponse, Error, SubscribePayload>(
        async (data) => {
            const response = await axiosClient.post('/ai/subscribe', data);
            return response.data;
        },
        {
            onSuccess: (data) => {
                // Si URL Stripe Checkout, rediriger vers Stripe
                if (data.url) {
                    window.location.href = data.url;
                    return;
                }
                // Sinon (paiement sur facture), invalider les queries
                queryClient.invalidateQueries('/ai/usage');
                queryClient.invalidateQueries('/ai/subscription');
            },
        }
    );
}

// Helper functions

export function getUsageLevel(percentageUsed: number): 'low' | 'medium' | 'high' | 'critical' {
    if (percentageUsed >= 100) return 'critical';
    if (percentageUsed >= 80) return 'high';
    if (percentageUsed >= 50) return 'medium';
    return 'low';
}

export function getUsageBgColor(percentageUsed: number): string {
    const level = getUsageLevel(percentageUsed);
    switch (level) {
        case 'critical':
            return 'bg-red-500';
        case 'high':
            return 'bg-orange-500';
        case 'medium':
            return 'bg-yellow-500';
        default:
            return 'bg-green-500';
    }
}
